import { useState, useRef, useCallback, type FormEvent, type DragEvent, type ChangeEvent } from 'react';
import { adminApi } from '@/services/admin';
import { useCreateSharedQuote } from '@/hooks/useSharedQuotes';
import { useToast } from '@/contexts/ToastContext';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { SharedQuoteDetail } from '@/services/shared-quotes';

// ---------------------------------------------
// Types
// ---------------------------------------------

interface SharedQuoteFormProps {
  readonly onSuccess: (quote: SharedQuoteDetail) => void;
}

interface AnalysisResult {
  readonly volumeCm3: number;
  readonly surfaceAreaCm2: number;
  readonly boundingBox: { readonly width: number; readonly height: number; readonly depth: number };
  readonly prices: readonly {
    readonly materialId: string;
    readonly price: number;
    readonly rawPrice: number;
    readonly estimatedDays: number;
  }[];
}

type AnalysisState = 'idle' | 'uploading' | 'analyzing' | 'done' | 'error';

// ---------------------------------------------
// Main component
// ---------------------------------------------

export const SharedQuoteForm = ({ onSuccess }: SharedQuoteFormProps) => {
  const toast = useToast();
  const createMutation = useCreateSharedQuote();

  // File upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [analysisState, setAnalysisState] = useState<AnalysisState>('idle');
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);

  // Form fields
  const [title, setTitle] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [message, setMessage] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('30');

  // General state
  const [error, setError] = useState<string | null>(null);

  const isUploading = analysisState === 'uploading' || analysisState === 'analyzing';
  const isSubmitting = createMutation.isPending;
  const isDisabled = isUploading || isSubmitting;

  // ---------------------------------------------
  // File upload + analysis
  // ---------------------------------------------

  const handleFileSelected = useCallback(async (selectedFile: File) => {
    const ext = selectedFile.name.toLowerCase().split('.').pop();
    const allowedExtensions = ['3mf', 'stl', 'obj', 'step', 'stp', 'amf'];
    if (!ext || !allowedExtensions.includes(ext)) {
      setAnalysisError('Unsupported file type. Accepted formats: .3mf, .stl, .obj, .step, .stp, .amf');
      setAnalysisState('error');
      return;
    }

    if (ext !== '3mf') {
      setAnalysisError('Only .3mf files support auto-analysis for volume and pricing. Please upload a 3MF file, or contact us for a manual quote on other formats.');
      setAnalysisState('error');
      return;
    }

    setFile(selectedFile);
    setFileKey(null);
    setAnalysisResult(null);
    setAnalysisError(null);
    setError(null);

    try {
      setAnalysisState('uploading');
      const { uploadUrl, fileKey: uploadedKey } = await adminApi.getModelUploadUrl(
        selectedFile.name,
        selectedFile.size,
        selectedFile.type || 'application/octet-stream',
      );
      await uploadToPresignedUrl(uploadUrl, selectedFile);
      setFileKey(uploadedKey);

      setAnalysisState('analyzing');
      const result = await adminApi.analyzeModel(uploadedKey);
      setAnalysisResult(result);
      setAnalysisState('done');
    } catch (err) {
      setAnalysisState('error');
      setAnalysisError(err instanceof Error ? err.message : 'Upload or analysis failed');
    }
  }, []);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) {
      handleFileSelected(dropped);
    }
  }, [handleFileSelected]);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleFileInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      handleFileSelected(selected);
    }
    // Reset input so re-selecting the same file triggers change
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileSelected]);

  // ---------------------------------------------
  // Form submit
  // ---------------------------------------------

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    if (!fileKey || !analysisResult || !file) {
      setError('Please upload and analyze a .3mf file first.');
      return;
    }

    const days = parseInt(expiresInDays, 10);
    if (isNaN(days) || days < 1) {
      setError('Expiration must be at least 1 day.');
      return;
    }

    try {
      const result = await createMutation.mutateAsync({
        title: title.trim(),
        customerName: customerName.trim() || null,
        customerEmail: customerEmail.trim() || null,
        message: message.trim() || null,
        fileKey,
        fileName: file.name,
        volumeCm3: analysisResult.volumeCm3,
        surfaceAreaCm2: analysisResult.surfaceAreaCm2,
        boundingBox: analysisResult.boundingBox,
        expiresInDays: days,
      });
      toast.success('Shared quote created successfully.');
      onSuccess(result);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create shared quote';
      setError(msg);
      toast.error(msg);
    }
  };

  // ---------------------------------------------
  // Render
  // ---------------------------------------------

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}
    >
      {error && (
        <div
          style={{
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--color-error-50)',
            padding: 'var(--space-3) var(--space-4)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-error-700)',
          }}
        >
          {error}
        </div>
      )}

      {/* --- File Upload Card --- */}
      <FormCard title="3D Model File">
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', margin: 0 }}>
          Upload a 3D model file for the quote. Auto-analysis with volume, dimensions, and pricing is available for 3MF files.
        </p>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary-600)', fontWeight: 'var(--font-weight-medium)', margin: 0 }}>
          Optimized for 3MF — stores assembly data, textures, and print settings for best results.
        </p>

        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          style={{
            border: `2px dashed ${isDragOver ? 'var(--color-primary-500)' : 'var(--border-default)'}`,
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-6)',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragOver ? 'var(--color-primary-50)' : 'var(--bg-surface-sunken)',
            transition: 'all var(--duration-fast) var(--ease-out)',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".3mf,.stl,.obj,.step,.stp,.amf"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          {file ? (
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-primary)', fontWeight: 'var(--font-weight-medium)' }}>
              {file.name}
            </p>
          ) : (
            <>
              <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                Drag and drop a 3D model file here, or click to browse
              </p>
              <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                Accepts .3mf, .stl, .obj, .step, .stp, .amf
              </p>
            </>
          )}
        </div>

        {isUploading && (
          <div
            className="flex items-center"
            style={{
              gap: 'var(--space-2)',
              padding: 'var(--space-3)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--bg-surface-sunken)',
            }}
          >
            <LoadingSpinner size="sm" />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
              {analysisState === 'uploading'
                ? 'Uploading for analysis...'
                : 'Analyzing model geometry and calculating prices...'}
            </span>
          </div>
        )}

        {analysisState === 'error' && (
          <div
            style={{
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-error-50)',
              padding: 'var(--space-3)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-error-700)',
            }}
          >
            {analysisError ?? 'Analysis failed'}
          </div>
        )}

        {analysisResult && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div
              className="grid"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 'var(--space-2)' }}
            >
              <AnalysisStat label="Volume" value={`${analysisResult.volumeCm3} cm3`} />
              <AnalysisStat label="Surface Area" value={`${analysisResult.surfaceAreaCm2} cm2`} />
              <AnalysisStat label="Width" value={`${(analysisResult.boundingBox.width * 10).toFixed(1)} mm`} />
              <AnalysisStat label="Height" value={`${(analysisResult.boundingBox.height * 10).toFixed(1)} mm`} />
              <AnalysisStat label="Depth" value={`${(analysisResult.boundingBox.depth * 10).toFixed(1)} mm`} />
            </div>
            <PriceTable prices={analysisResult.prices} />
          </div>
        )}
      </FormCard>

      {/* --- Quote Details Card --- */}
      <FormCard title="Quote Details">
        <Input
          id="sq-title"
          label="Title"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Custom enclosure for client project"
        />

        <Input
          id="sq-customer-name"
          label="Customer Name"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          placeholder="Optional"
        />

        <Input
          id="sq-customer-email"
          label="Customer Email"
          type="email"
          value={customerEmail}
          onChange={(e) => setCustomerEmail(e.target.value)}
          placeholder="Optional - used for sending quote link"
        />

        <div>
          <label
            htmlFor="sq-message"
            style={{
              display: 'block',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-secondary)',
            }}
          >
            Message
          </label>
          <textarea
            id="sq-message"
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="admin-input"
            placeholder="Optional note to include with the quote"
            style={{
              marginTop: 'var(--space-1)',
              display: 'block',
              width: '100%',
              resize: 'vertical',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-default)',
              padding: 'var(--space-2) var(--space-3)',
              fontSize: 'var(--text-base)',
              backgroundColor: 'var(--bg-surface)',
              color: 'var(--text-primary)',
              outline: 'none',
            }}
          />
        </div>

        <Input
          id="sq-expires"
          label="Expires In (days)"
          type="number"
          min="1"
          max="90"
          value={expiresInDays}
          onChange={(e) => setExpiresInDays(e.target.value)}
        />
      </FormCard>

      {/* --- Actions --- */}
      <div className="flex" style={{ gap: 'var(--space-3)' }}>
        <Button
          type="submit"
          disabled={isDisabled || !analysisResult || !title.trim()}
        >
          {isSubmitting ? 'Creating...' : 'Create Quote'}
        </Button>
      </div>
    </form>
  );
};

// ---------------------------------------------
// Upload helper
// ---------------------------------------------

const uploadToPresignedUrl = (url: string, file: File): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.send(file);
  });
};

// ---------------------------------------------
// FormCard wrapper (matches admin pattern)
// ---------------------------------------------

const FormCard = ({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}) => (
  <div
    style={{
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border-default)',
      backgroundColor: 'var(--bg-surface)',
      padding: 'var(--space-5)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-4)',
    }}
  >
    <h2
      style={{
        fontSize: 'var(--text-base)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--text-primary)',
        margin: 0,
      }}
    >
      {title}
    </h2>
    {children}
  </div>
);

// ---------------------------------------------
// Analysis stat chip
// ---------------------------------------------

const AnalysisStat = ({ label, value }: { readonly label: string; readonly value: string }) => (
  <div
    style={{
      padding: 'var(--space-2) var(--space-3)',
      borderRadius: 'var(--radius-md)',
      backgroundColor: 'var(--bg-surface-sunken)',
      border: '1px solid var(--border-default)',
    }}
  >
    <p
      style={{
        fontSize: 'var(--text-xs)',
        color: 'var(--text-tertiary)',
        margin: 0,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </p>
    <p
      style={{
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--text-primary)',
        margin: '2px 0 0',
      }}
    >
      {value}
    </p>
  </div>
);

// ---------------------------------------------
// Price table
// ---------------------------------------------

const PriceTable = ({
  prices,
}: {
  readonly prices: readonly {
    readonly materialId: string;
    readonly price: number;
    readonly rawPrice: number;
    readonly estimatedDays: number;
  }[];
}) => {
  if (prices.length === 0) return null;

  const cheapest = Math.min(...prices.map((p) => p.price));

  return (
    <div
      style={{
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-default)',
        backgroundColor: 'var(--bg-surface-sunken)',
        padding: 'var(--space-3)',
      }}
    >
      <p
        style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-secondary)',
          margin: '0 0 var(--space-2) 0',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        Prices by Material
      </p>
      <div
        className="grid"
        style={{
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 'var(--space-2)',
        }}
      >
        {prices.map((p) => (
          <div
            key={p.materialId}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--space-1) var(--space-2)',
              borderRadius: 'var(--radius-sm)',
              backgroundColor: p.price === cheapest ? 'var(--color-success-50)' : 'var(--bg-surface)',
              border: p.price === cheapest
                ? '1px solid var(--color-success-500)'
                : '1px solid var(--border-default)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-primary)',
              }}
            >
              {p.materialId}
            </span>
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: p.price === cheapest ? 'var(--color-success-600)' : 'var(--text-primary)',
              }}
            >
              ${p.price.toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
